(() => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Intl is only the fallback. Prices are pre-formatted per variant in Liquid ([data-jwl-money]) so the text after a
     variant change is byte-identical to the first render: Shopify's money format and Intl disagree on symbol position
     and spacing in several of the store's currencies, and dividing in JS rounds where Liquid floors (54.95 / 3 is
     18.31 in Liquid, 18.32 via Math.round). */
  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP'
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `£${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  class JwlProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.form = this.querySelector('form.jwl-product-form');
      this.variantSelect = this.querySelector('[data-jwl-variant-select]');
      this.primaryButton = this.querySelector('[data-jwl-add-button]');
      this.formError = this.querySelector('[data-jwl-form-error]');
      this.status = this.querySelector('[data-jwl-status]');
      this.currency = this.dataset.currency || 'GBP';
      this.busy = false;

      try {
        this.variants = JSON.parse(this.querySelector('[data-jwl-variants]')?.textContent || '[]');
      } catch (_) {
        this.variants = [];
      }
      try {
        this.money = JSON.parse(this.querySelector('[data-jwl-money]')?.textContent || '{}');
      } catch (_) {
        this.money = {};
      }

      this.currentVariant = this.variants.find((variant) => String(variant.id) === this.variantSelect?.value) || this.variants[0];
      this.bindMetalOptions();
      this.bindVariantFallback();
      this.bindGallery();
      this.bindGemstoneCarousel();
      this.bindEngraving();
      this.bindProductForm();
      this.bindStickyButton();
      this.updateVariantUI(this.currentVariant, false);
    }

    bindMetalOptions() {
      this.querySelectorAll('[data-jwl-metal-option]').forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;
          const optionIndex = Number(button.dataset.optionIndex);
          const desiredValue = button.dataset.value;
          const currentOptions = [...(this.currentVariant?.options || [])];
          currentOptions[optionIndex] = desiredValue;

          const exactVariant = this.variants.find((variant) =>
            variant.options.every((option, index) => option === currentOptions[index])
          );
          const availableVariant = this.variants.find((variant) =>
            variant.options[optionIndex] === desiredValue && variant.available
          );
          const nextVariant = exactVariant?.available ? exactVariant : (availableVariant || exactVariant);

          if (nextVariant) this.updateVariantUI(nextVariant, true);
        });
      });
    }

    bindVariantFallback() {
      this.variantSelect?.addEventListener('change', () => {
        const variant = this.variants.find((item) => String(item.id) === this.variantSelect.value);
        if (variant) this.updateVariantUI(variant, true);
      });
    }

    bindGallery() {
      this.galleryViewport = this.querySelector('[data-jwl-gallery-viewport]');
      this.mediaSlides = [...this.querySelectorAll('[data-jwl-media-slide]')];
      this.mediaThumbs = [...this.querySelectorAll('[data-jwl-media-thumb]')];

      this.mediaThumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showMedia(thumb.dataset.mediaId, true));
        thumb.addEventListener('keydown', (event) => {
          const index = this.mediaThumbs.indexOf(thumb);
          let nextIndex = index;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = Math.min(index + 1, this.mediaThumbs.length - 1);
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = Math.max(index - 1, 0);
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = this.mediaThumbs.length - 1;
          if (nextIndex !== index) {
            event.preventDefault();
            const nextThumb = this.mediaThumbs[nextIndex];
            this.showMedia(nextThumb.dataset.mediaId, true);
          }
        });
      });

      if (this.galleryViewport && 'IntersectionObserver' in window) {
        this.mediaObserver = new IntersectionObserver((entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (visible) this.setActiveThumb(visible.target.dataset.mediaId);
        }, { root: this.galleryViewport, threshold: [0.6, 0.85] });
        this.mediaSlides.forEach((slide) => this.mediaObserver.observe(slide));
      }
    }

    showMedia(mediaId, shouldFocus) {
      if (!mediaId) return;
      const slide = this.mediaSlides.find((item) => item.dataset.mediaId === String(mediaId));
      if (!slide || !this.galleryViewport) return;

      this.galleryViewport.scrollTo({
        left: slide.offsetLeft,
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });
      this.setActiveThumb(mediaId);
      if (shouldFocus) {
        const thumb = this.mediaThumbs.find((item) => item.dataset.mediaId === String(mediaId));
        thumb?.focus({ preventScroll: true });
      }

      this.mediaSlides.forEach((item) => {
        if (item !== slide) item.querySelectorAll('video').forEach((video) => video.pause());
      });
    }

    setActiveThumb(mediaId) {
      this.mediaThumbs.forEach((thumb) => {
        const active = thumb.dataset.mediaId === String(mediaId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', String(active));
        thumb.tabIndex = active ? 0 : -1;
      });
      this.mediaSlides.forEach((slide) => slide.classList.toggle('is-active', slide.dataset.mediaId === String(mediaId)));
    }

    bindGemstoneCarousel() {
      const track = this.querySelector('[data-jwl-scroll-region]');
      if (!track) return;
      const amount = () => Math.max(track.clientWidth * 0.75, 220);
      this.querySelector('[data-jwl-scroll-previous]')?.addEventListener('click', () => {
        track.scrollBy({ left: -amount(), behavior: prefersReducedMotion ? 'auto' : 'smooth' });
      });
      this.querySelector('[data-jwl-scroll-next]')?.addEventListener('click', () => {
        track.scrollBy({ left: amount(), behavior: prefersReducedMotion ? 'auto' : 'smooth' });
      });
    }

    bindEngraving() {
      this.engraving = this.querySelector('[data-jwl-engraving]');
      if (!this.engraving) return;

      this.engravingToggle = this.engraving.querySelector('[data-jwl-engraving-toggle]');
      this.engravingField = this.engraving.querySelector('[data-jwl-engraving-field]');
      this.engravingInput = this.engraving.querySelector('[data-jwl-engraving-input]');
      this.engravingCount = this.engraving.querySelector('[data-jwl-engraving-count]');
      /* The paid-engraving marker the store's Cart Transform charges on (properties[_engraving_fee]). It is enabled
         only while engraving is switched on: a disabled input is not submitted, so an un-engraved pendant can never
         carry the fee. */
      this.engravingFee = this.engraving.querySelector('[data-jwl-engraving-fee]');

      this.engravingToggle?.addEventListener('change', () => {
        const enabled = this.engravingToggle.checked;
        this.engravingField.hidden = !enabled;
        this.engravingInput.disabled = !enabled;
        if (this.engravingFee) this.engravingFee.disabled = !enabled;
        if (enabled) this.engravingInput.focus();
        if (!enabled) {
          this.engravingInput.value = '';
          this.updateEngravingCount();
        }
      });
      this.engravingInput?.addEventListener('input', () => this.updateEngravingCount());
    }

    updateEngravingCount() {
      if (this.engravingCount) this.engravingCount.textContent = String(this.engravingInput?.value.length || 0);
    }

    /* PRESTIGE CART DRAWER
       The form sits inside Prestige's <product-form>, which intercepts the submit, posts to /cart/add.js and fires
       cart:change with baseEvent "variant:add" -- the event that opens the existing drawer. So a standard purchase
       here behaves exactly like every other product on the store, and there is no second drawer.

       Two listeners now see the same submit, and the order matters:
         1. This one runs FIRST. It is a capture listener on <jwl-product>, an ancestor of the form, so it fires
            before any listener on the form itself. When it has to block (engraving switched on with no wording),
            stopPropagation keeps the event from ever reaching Prestige -- otherwise Prestige would add the pendant
            anyway, without the engraving the shopper asked for.
         2. It must NOT mark the button busy synchronously. Prestige's handler opens with
            `if (event.submitter.getAttribute("aria-busy") === "true") return;` -- setting aria-busy here first made
            it return and add nothing. The busy state is applied a tick later, after Prestige has started.
       Completion comes back as variant:add (success) or cart:error (failure) on the form. */
    bindProductForm() {
      if (!this.form) return;
      this.prestigeForm = Boolean(this.form.closest('product-form') && window.customElements.get('product-form'));

      this.addEventListener('submit', async (event) => {
        if (event.target !== this.form) return;
        const block = () => { event.preventDefault(); event.stopPropagation(); };

        if (this.busy) { block(); return; }

        const engravingEnabled = Boolean(this.engravingToggle?.checked);
        const engravingConfigured = this.engraving?.dataset.jwlEngravingConfigured === 'true';
        const addOnVariantId = this.engraving?.dataset.jwlEngravingAddon?.trim();

        if (engravingEnabled && !engravingConfigured) {
          block();
          this.showError('Engraving cannot be added until its paid integration is connected.');
          return;
        }

        if (engravingEnabled && !this.engravingInput?.value.trim()) {
          block();
          this.showError('Please enter the wording for your engraving.');
          this.engravingInput?.focus();
          return;
        }

        this.clearError();
        if (engravingEnabled && addOnVariantId) {
          block();
          await this.addProductWithEngraving(addOnVariantId);
          return;
        }

        this.dispatchEvent(new CustomEvent('jwl:product-form:submit', {
          bubbles: true,
          detail: { form: this.form, variant: this.currentVariant }
        }));

        if (this.prestigeForm) {
          window.setTimeout(() => this.setBusy(true), 0);
          window.clearTimeout(this.busyGuard);
          this.busyGuard = window.setTimeout(() => this.setBusy(false), 15000);
        } else {
          this.setBusy(true);
        }
      }, true);

      this.form.addEventListener('variant:add', () => {
        window.clearTimeout(this.busyGuard);
        this.setAddedState();
      });
      this.form.addEventListener('cart:error', (event) => {
        window.clearTimeout(this.busyGuard);
        this.showError(event.detail?.error || 'Unable to add this item. Please try again.');
        this.setBusy(false);
      });
      this.addEventListener('jwl:product-form:reset', () => this.setBusy(false));
    }

    async addProductWithEngraving(addOnVariantId) {
      this.setBusy(true);
      if (!/^\d+$/.test(String(addOnVariantId))) {
        this.showError('The engraving add-on variant ID is invalid.');
        this.setBusy(false);
        return;
      }
      const formData = new FormData(this.form);
      const quantity = Number(formData.get('quantity') || 1);
      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.*)]$/);
        if (match && String(value).trim()) properties[match[1]] = value;
      }

      /* This path bypasses the form, so it speaks to Prestige's drawer the same way Prestige's own ProductForm does:
         collect the drawer's bundled sections first, then announce the add as cart:change / baseEvent "variant:add",
         which is what opens and re-renders the existing drawer. Only used when an add-on variant ID is configured;
         the store's engraving normally charges through the Cart Transform on a single line instead. */
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items: [
              { id: Number(this.currentVariant.id), quantity, properties },
              {
                id: Number(addOnVariantId),
                quantity: 1,
                properties: { '_JWL linked product': String(this.currentVariant.id) }
              }
            ],
            sections: sections.join(','),
            sections_url: `${root}variants/${this.currentVariant.id}`
          })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this item.');

        const cart = await (await fetch(`${root}cart.js`)).json();
        cart.sections = result.sections;
        this.form.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: { items: result.items || [result], cart }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart }
        }));

        this.setAddedState();
        this.dispatchEvent(new CustomEvent('jwl:cart:added', {
          bubbles: true,
          detail: { result, form: this.form, variant: this.currentVariant, engravingAddOnVariantId: addOnVariantId }
        }));
      } catch (error) {
        this.showError(error.message || 'Unable to add this item. Please try again.');
        this.setBusy(false);
      }
    }

    bindStickyButton() {
      this.stickyBar = this.querySelector('[data-jwl-sticky-atc]');
      this.stickyButton = this.querySelector('[data-jwl-sticky-submit]');
      if (!this.stickyBar || !this.primaryButton) return;

      this.stickyBar.hidden = false;
      document.documentElement.classList.add('jwl-sticky-atc-active');
      this.stickyButton?.addEventListener('click', () => this.primaryButton.click());
    }

    updateVariantUI(variant, announce) {
      if (!variant) return;
      this.currentVariant = variant;
      if (this.variantSelect) this.variantSelect.value = String(variant.id);

      const currentPrice = this.querySelector('[data-jwl-price-current]');
      const comparePrice = this.querySelector('[data-jwl-price-compare]');
      const saleLabel = this.querySelector('[data-jwl-sale-label]');
      const installment = this.querySelector('[data-jwl-payment-installment]');
      const onSale = Number(variant.compare_at_price) > Number(variant.price);
      const money = this.money[String(variant.id)] || {};
      const formattedPrice = money.price || formatMoney(variant.price, this.currency);

      if (currentPrice) currentPrice.textContent = formattedPrice;
      if (comparePrice) {
        comparePrice.textContent = onSale ? (money.compare || formatMoney(variant.compare_at_price, this.currency)) : '';
        comparePrice.classList.toggle('jwl-is-hidden', !onSale);
      }
      saleLabel?.classList.toggle('jwl-is-hidden', !onSale);
      if (installment) installment.textContent = money.instalment || formatMoney(Math.floor(Number(variant.price) / 3), this.currency);

      this.querySelectorAll('[data-jwl-metal-option]').forEach((button) => {
        const index = Number(button.dataset.optionIndex);
        const selected = variant.options[index] === button.dataset.value;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      const selectedMetal = this.querySelector('[data-jwl-metal-option][aria-pressed="true"]')?.dataset.value || '';
      const metalLabel = this.querySelector('[data-jwl-metal-label]');
      const stickyMetal = this.querySelector('[data-jwl-sticky-metal]');
      if (metalLabel) metalLabel.textContent = selectedMetal;
      if (stickyMetal) stickyMetal.textContent = selectedMetal;

      const available = Boolean(variant.available);
      this.updateButton(this.primaryButton, available);
      this.updateButton(this.stickyButton, available);
      const stickyPrice = this.querySelector('[data-jwl-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = formattedPrice;

      const featuredMediaId = variant.featured_media?.id || variant.featured_media_id;
      if (featuredMediaId) this.showMedia(featuredMediaId, false);

      if (this.dataset.updateUrl === 'true' && history.replaceState) {
        const url = new URL(this.dataset.productUrl || window.location.pathname, window.location.origin);
        url.searchParams.set('variant', variant.id);
        history.replaceState({ path: url.toString() }, '', url.toString());
      }

      if (announce && this.status) {
        this.status.textContent = `${variant.title}. ${formattedPrice}. ${available ? 'Available' : 'Sold out'}.`;
      }
      this.dispatchEvent(new CustomEvent('jwl:variant:change', { bubbles: true, detail: { variant } }));
    }

    updateButton(button, available) {
      if (!button) return;
      button.disabled = !available;
      const label = button.querySelector('[data-jwl-add-label], [data-jwl-sticky-label]');
      if (!label) return;
      label.textContent = available
        ? (this.primaryButton?.dataset.labelAvailable || 'Add to Bag')
        : (this.primaryButton?.dataset.labelSoldOut || 'Sold Out');
    }

    setBusy(busy) {
      this.busy = busy;
      if (this.primaryButton) {
        this.primaryButton.disabled = busy || !this.currentVariant?.available;
        this.primaryButton.setAttribute('aria-busy', String(busy));
        const label = this.primaryButton.querySelector('[data-jwl-add-label]');
        if (label) label.textContent = busy
          ? (this.primaryButton.dataset.labelAdding || 'Adding…')
          : (this.currentVariant?.available ? this.primaryButton.dataset.labelAvailable : this.primaryButton.dataset.labelSoldOut);
      }
      if (this.stickyButton) this.stickyButton.disabled = busy || !this.currentVariant?.available;
    }

    setAddedState() {
      const label = this.primaryButton?.querySelector('[data-jwl-add-label]');
      const stickyLabel = this.stickyButton?.querySelector('[data-jwl-sticky-label]');
      if (label) label.textContent = this.primaryButton.dataset.labelAdded || 'Added';
      if (stickyLabel) stickyLabel.textContent = this.primaryButton.dataset.labelAdded || 'Added';
      if (this.status) this.status.textContent = 'Added to bag.';
      window.setTimeout(() => {
        this.setBusy(false);
        this.updateButton(this.stickyButton, this.currentVariant?.available);
      }, 1400);
    }

    showError(message) {
      if (!this.formError) return;
      this.formError.textContent = message;
      this.formError.hidden = false;
    }

    clearError() {
      if (!this.formError) return;
      this.formError.textContent = '';
      this.formError.hidden = true;
    }
  }

  class JwlProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.loaded) return;
      this.loaded = true;
      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const replacement = html.querySelector('jwl-product-recommendations');
          if (replacement?.innerHTML.trim()) {
            this.innerHTML = replacement.innerHTML;
          } else {
            this.closest('.jwl-recommendations')?.setAttribute('hidden', '');
          }
        })
        .catch(() => this.closest('.jwl-recommendations')?.setAttribute('hidden', ''));
    }
  }

  if (!customElements.get('jwl-product')) customElements.define('jwl-product', JwlProduct);
  if (!customElements.get('jwl-product-recommendations')) customElements.define('jwl-product-recommendations', JwlProductRecommendations);
})();
